import { useLocation } from 'react-router-dom';
import { Radar } from 'react-chartjs-2';
import { Chart, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import '@styles/Stats.css';
import '@styles/muscledistribution.css';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getMuscleDistribution } from '@utility/stats_utils';
import { formatCompactNumber } from '@utility/common_utils';

Chart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const MuscleDistribution = () => {
  const { state } = useLocation();
  const [viewMode, setViewMode] = useState('specific');
  const [muscleMode, setMuscleMode] = useState('total');
  const [visibleMetrics, setVisibleMetrics] = useState({
    volume: true,
    reps: true,
    sets: true
  });
  const [useExact, setUseExact] = useState(false);
  const [muscleData, setMuscleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currSetting, setCurrSetting] = useState("isolated");
  const [timeRange, setTimeRange] = useState('all_time');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [compareData, setCompareData] = useState([]);

  const fetchMuscleData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let startDate, endDate;
      
      // Calculate dates based on selected time range
      const now = new Date();
      switch(timeRange) {
        case 'current_month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'last_3_months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'last_6_months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'custom':
          startDate = customStartDate;
          endDate = customEndDate;
          break;
        default: // all_time
          startDate = null;
          endDate = null;
      }
      
      const data = await getMuscleDistribution(muscleMode, useExact, startDate, endDate);
      setMuscleData(Array.isArray(data) ? data : []);

      if (compareEnabled && startDate && endDate && timeRange !== 'all_time') {
        const { compareStart, compareEnd } = getComparisonRange(startDate, endDate);
        if (compareStart && compareEnd) {
          const compareDataResult = await getMuscleDistribution(muscleMode, useExact, compareStart, compareEnd);
          setCompareData(Array.isArray(compareDataResult) ? compareDataResult : []);
        } else {
          setCompareData([]);
        }
      } else {
        setCompareData([]);
      }
    } catch (err) {
      console.error('Error fetching muscle data:', err);
      setError('Failed to load muscle distribution data');
      setMuscleData([]);
    } finally {
      setLoading(false);
    }
  }, [muscleMode, useExact, timeRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchMuscleData();
  }, [fetchMuscleData]);

  // Filter and validate muscle data
  const validData = useMemo(() => {
    return Array.isArray(muscleData) 
      ? muscleData.filter(item => 
          item?.muscle && 
          (item?.volume > 0 || item?.reps > 0 || item?.sets > 0)
        )
      : [];
  }, [muscleData]);

  const validCompareData = useMemo(() => {
    return Array.isArray(compareData) 
      ? compareData.filter(item => 
          item?.muscle && 
          (item?.volume > 0 || item?.reps > 0 || item?.sets > 0)
        )
      : [];
  }, [compareData]);

  const groupedCompareData = useMemo(() => {
    if (viewMode === 'group') {
      return groupMusclesByGroup(validCompareData);
    }
    return validCompareData.slice().sort((a, b) => (b.volume || 0) - (a.volume || 0));
  }, [viewMode, validCompareData]);
  
  const groupedData = useMemo(() => {
    if (viewMode === 'group') {
      return groupMusclesByGroup(validData);
    }
    return validData.slice().sort((a, b) => (b.volume || 0) - (a.volume || 0));
  }, [viewMode, validData]);

  const mergedData = useMemo(() => {
    const allMuscles = new Set([
      ...groupedData.map(item => item.muscle),
      ...groupedCompareData.map(item => item.muscle)
    ]);

    return Array.from(allMuscles).map(muscleName => {
      const currentData = groupedData.find(item => item.muscle === muscleName) || 
        { muscle: muscleName, volume: 0, reps: 0, sets: 0 };
      const compareDataItem = groupedCompareData.find(item => item.muscle === muscleName) || 
        { muscle: muscleName, volume: 0, reps: 0, sets: 0 };
      
      return {
        current: currentData,
        compare: compareDataItem
      };
    }).sort((a, b) => (b.current.volume || 0) - (a.current.volume || 0));
  }, [groupedData, groupedCompareData]);

  const getComparisonRange = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return { compareStart: null, compareEnd: null };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const rangeDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    const compareEnd = new Date(start);
    compareEnd.setDate(compareEnd.getDate() - 1); // End one day before current start
    
    const compareStart = new Date(compareEnd);
    compareStart.setDate(compareStart.getDate() - rangeDays + 1);
    
    return { compareStart, compareEnd };
  }, []);

  // Calculate max values for each metric
  const maxValues = useMemo(() => ({
    volume: Math.max(...groupedData.map(item => item.volume || 0), 1),
    reps: Math.max(...groupedData.map(item => item.reps || 0), 1),
    sets: Math.max(...groupedData.map(item => item.sets || 0), 1)
  }), [groupedData]);

  // Generate chart data
  const chartData = useMemo(() => {
    const dataToUse = compareEnabled && validCompareData.length > 0 ? mergedData : groupedData.map(item => ({ current: item }));
    const labels = dataToUse.map(item => item.current.muscle);

    const datasets = [
      {
        label: 'Volume (kg)',
        data: dataToUse.map(item => (item.current.volume || 0) / maxValues.volume),
        rawData: dataToUse.map(item => item.current.volume || 0),
        backgroundColor: 'rgba(00, 190, 00, 0.2)',
        borderColor: 'rgba(00, 255, 00, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(00, 255, 00, 1)',
        pointBorderColor: '#333',
        pointHoverRadius: 6,
        pointRadius: 4,
        hidden: !visibleMetrics.volume
      },
      {
        label: 'Total Reps',
        data: dataToUse.map(item => (item.current.reps || 0) / maxValues.reps),
        rawData: dataToUse.map(item => item.current.reps || 0),
        backgroundColor: 'rgba(190, 00, 00, 0.2)',
        borderColor: 'rgba(255, 00, 00, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(255, 00, 00, 1)',
        pointBorderColor: '#333',
        pointHoverRadius: 6,
        pointRadius: 4,
        hidden: !visibleMetrics.reps
      },
      {
        label: 'Total Sets',
        data: dataToUse.map(item => (item.current.sets || 0) / maxValues.sets),
        rawData: dataToUse.map(item => item.current.sets || 0),
        backgroundColor: 'rgba(00, 00, 255, 0.2)',
        borderColor: 'rgba(00, 00, 190, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(00, 00, 255, 1)',
        pointBorderColor: '#333',
        pointHoverRadius: 6,
        pointRadius: 4,
        hidden: !visibleMetrics.sets
      }
    ];

    // Add comparison datasets if comparison is enabled
    if (compareEnabled && validCompareData.length > 0) {
      datasets.push(
        {
          label: 'Volume (kg) - Previous',
          data: dataToUse.map(item => (item.compare?.volume || 0) / maxValues.volume),
          rawData: dataToUse.map(item => item.compare?.volume || 0),
          backgroundColor: 'rgba(00, 95, 00, 0.2)',
          borderColor: 'rgba(00, 128, 00, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(00, 128, 00, 1)',
          pointBorderColor: '#333',
          pointHoverRadius: 6,
          pointRadius: 4,
          borderDash: [5, 5],
          hidden: !visibleMetrics.volume
        },
        {
          label: 'Total Reps - Previous',
          data: dataToUse.map(item => (item.compare?.reps || 0) / maxValues.reps),
          rawData: dataToUse.map(item => item.compare?.reps || 0),
          backgroundColor: 'rgba(95, 00, 00, 0.2)',
          borderColor: 'rgba(128, 00, 00, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(128, 00, 00, 1)',
          pointBorderColor: '#333',
          pointHoverRadius: 6,
          pointRadius: 4,
          borderDash: [5, 5],
          hidden: !visibleMetrics.reps
        },
        {
          label: 'Total Sets - Previous',
          data: dataToUse.map(item => (item.compare?.sets || 0) / maxValues.sets),
          rawData: dataToUse.map(item => item.compare?.sets || 0),
          backgroundColor: 'rgba(00, 00, 128, 0.2)',
          borderColor: 'rgba(00, 00, 95, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(00, 00, 128, 1)',
          pointBorderColor: '#333',
          pointHoverRadius: 6,
          pointRadius: 4,
          borderDash: [5, 5],
          hidden: !visibleMetrics.sets
        }
      );
    }

    return { labels, datasets };
  }, [groupedData, mergedData, maxValues, visibleMetrics, compareEnabled, validCompareData]);

  // Chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { 
          display: true, 
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        suggestedMin: 0,
        suggestedMax: 1.1,
        ticks: { 
          display: false,
          stepSize: 0.2
        },
        grid: { 
          circular: false,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: { 
          font: { size: 12 },
          color: '#fff',
          callback: function(label) {
            // Truncate long muscle names
            return label.length > 12 ? label.substring(0, 12) + '...' : label;
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: { size: 12 }
        },
        onClick: (e, legendItem) => {
          const index = legendItem.datasetIndex;
          const metric = ['volume', 'reps', 'sets'][index % 3];
          toggleMetric(metric);
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          title: (context) => {
            return context[0]?.label || '';
          },
          label: (context) => {
            const raw = context.dataset.rawData?.[context.dataIndex];
            const label = context.dataset.label;
            if (label.includes('Volume')) {
              return `${label}: ${raw?.toLocaleString() || 0} kg`;
            }
            return `${label}: ${raw?.toLocaleString() || 0}`;
          }
        }
      }
    },
    elements: { 
      line: { tension: 0.2 },
      point: { hoverBorderWidth: 2 }
    },
    interaction: {
      intersect: false,
      mode: 'point'
    }
  }), []);

  // Helper function to group muscles by their groups
  function groupMusclesByGroup(data) {
    const groupsMap = {};
    
    data.forEach(item => {
      const groupId = item.group_id;
      const groupName = item.group_name || 'Unknown Group';
      
      if (!groupId) return; // Skip items without group info
      
      if (!groupsMap[groupId]) {
        groupsMap[groupId] = {
          muscle: groupName,
          group_id: groupId,
          volume: 0,
          reps: 0,
          sets: 0
        };
      }
      
      groupsMap[groupId].volume += item.volume || 0;
      groupsMap[groupId].reps += item.reps || 0;
      groupsMap[groupId].sets += item.sets || 0;
    });
    
    return Object.values(groupsMap)
      .sort((a, b) => (b.volume || 0) - (a.volume || 0));
  }


  const toggleMetric = useCallback((metric) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleMuscleModeChange = useCallback((mode) => {
    setMuscleMode(mode);
  }, []);

  const handleUseExactChange = useCallback(() => {
    setUseExact(prev => !prev);
  }, []);

  const handleCompareToggle = useCallback(() => {
    setCompareEnabled(prev => !prev);
  }, []);

  const renderSetting = () => {
    switch (currSetting) {
      case 'group':
        return <div className="toggle-group">
              <label>
                <input
                  type="radio"
                  name="viewMode"
                  checked={viewMode === 'specific'}
                  onChange={() => handleViewModeChange('specific')}
                />
                Specific Muscles
              </label>
              <label>
                <input
                  type="radio"
                  name="viewMode"
                  checked={viewMode === 'group'}
                  onChange={() => handleViewModeChange('group')}
                />
                Muscle Groups
              </label>
            </div>;
      case 'exact':
        return <div className="toggle-group">
              <label>
                <input
                  type="checkbox"
                  checked={useExact}
                  onChange={handleUseExactChange}
                />
                Exact Muscle Volume
              </label>
            </div>;
      case 'isolated':
        return <div className="toggle-group">
              <label>
                <input
                  type="radio"
                  name="muscleMode"
                  checked={muscleMode === 'total'}
                  onChange={() => handleMuscleModeChange('total')}
                />
                Total
              </label>
              <label>
                <input
                  type="radio"
                  name="muscleMode"
                  checked={muscleMode === 'primary'}
                  onChange={() => handleMuscleModeChange('primary')}
                />
                Primary Only
              </label>
              <label>
                <input
                  type="radio"
                  name="muscleMode"
                  checked={muscleMode === 'secondary'}
                  onChange={() => handleMuscleModeChange('secondary')}
                />
                Secondary Only
              </label>
            </div>;
      case 'metric':
        return <div className="time-range-section">
                  <TimeRangeSelector />

                  <div className="toggle-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={compareEnabled}
                        onChange={handleCompareToggle}
                        disabled={timeRange === 'all_time'}
                      />
                      Compare with previous range selected.
                    </label>
                  </div>
                </div>
      default:
        return null;
    }
  }

  const TimeRangeSelector = () => {
    const timeOptions = [
      { value: 'all_time', label: 'All Time' },
      { value: 'current_month', label: 'Current Month' },
      { value: 'last_3_months', label: 'Last 3 Months' },
      { value: 'last_6_months', label: 'Last 6 Months' },
      { value: 'custom', label: 'Custom Range' }
    ];

    return (
      <div className="time-range-selector">
        <div className="time-range-buttons">
          {timeOptions.map(option => (
            <button
              key={option.value}
              className={`time-range-btn ${timeRange === option.value ? 'active' : ''}`}
              onClick={() => {
                setTimeRange(option.value);
                if (option.value !== 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        
        {timeRange === 'custom' && (
          <div className="custom-date-picker">
            <label>
              Start Date:
              <input 
                type="date" 
                value={customStartDate || ''}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </label>
            <label>
              End Date:
              <input 
                type="date" 
                value={customEndDate || ''}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="loading-spinner">Loading muscle distribution data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchMuscleData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content calendar-page">
          <div className="button-box">
            <button className={`btn-disabled ${currSetting === 'group' ? 'btn-active' : ''}`} onClick={() => setCurrSetting("group")}>Grouping</button>
            <button className={`btn-disabled ${currSetting === 'exact' ? 'btn-active' : ''}`} onClick={() => setCurrSetting("exact")}>Exact</button>
            <button className={`btn-disabled ${currSetting === 'isolated' ? 'btn-active' : ''}`} onClick={() => setCurrSetting("isolated")}>Focus</button>
            <button className={`btn-disabled ${currSetting === 'metric' ? 'btn-active' : ''}`} onClick={() => setCurrSetting("metric")}>Range</button>
          </div>

          <div className="view-mode-toggles">
            {renderSetting()}
          </div>

          {groupedData.length > 0 ? (
          <>
          <div className="chart-container" style={{ height: '500px', width: '100%', position: 'relative' }}>
            <Radar 
              data={chartData}
              options={chartOptions}
            />
          </div>

          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="muscle-column">{viewMode === 'group' ? 'Muscle Group' : 'Muscle'}</th>
                  <th className="number-column">Volume (kg)</th>
                  <th className="number-column">Reps</th>
                  <th className="number-column">Sets</th>
                  <th className="number-column">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {groupedData.map((item, index) => {
                  const totalVolume = groupedData.reduce((sum, i) => sum + (i.volume || 0), 0);
                  const percentage = totalVolume > 0 ? ((item.volume || 0) / totalVolume * 100).toFixed(1) : '0.0';
                  
                  return (
                    <tr key={index}>
                      <td className="muscle-column">{item.muscle}</td>
                      <td className="number-column">{formatCompactNumber(item.volume || 0)}</td>
                      <td className="number-column">{formatCompactNumber(item.reps || 0)}</td>
                      <td className="number-column">{formatCompactNumber(item.sets || 0)}</td>
                      <td className="number-column percentage">{percentage}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="summary-stats">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Volume</h3>
                <p>{groupedData.reduce((sum, item) => sum + (item.volume || 0), 0).toLocaleString()} kg</p>
              </div>
              <div className="stat-card">
                <h3>Total Reps</h3>
                <p>{groupedData.reduce((sum, item) => sum + (item.reps || 0), 0).toLocaleString()}</p>
              </div>
              <div className="stat-card">
                <h3>Total Sets</h3>
                <p>{groupedData.reduce((sum, item) => sum + (item.sets || 0), 0).toLocaleString()}</p>
              </div>
              <div className="stat-card">
                <h3>{viewMode === 'group' ? 'Muscle Groups' : 'Muscles'} Trained</h3>
                <p>{groupedData.length}</p>
              </div>
            </div>
          </div>
          </>
          ) : (
            <>
          <div className="no-data">
            <p>No data available in this range</p>
            <p>Broaden the range to see statistics.</p>
          </div>
          </>
        )}
    </div>
  );
};

export default MuscleDistribution;